import React from 'react';
import './rush.css';
import './features.css';
import './feature-detail.css';
import { RushNav, RushFooter, useRushReveal } from './RushChrome';
import { FI, FeatHero, FeatCapabilities, FeatDetailRow, FeatSteps, FeatQuote, FeatCrossLinks, FeatFinalCTA } from './RushFeatureLayout';

const RISK = '#d2604f';

const AQHeroVisual = () => (
  <div className="mock-frame">
    <div className="mock-chrome"><span className="mc-dot"></span><span className="mc-dot"></span><span className="mc-dot"></span><span className="mc-title">AI quality · 248 segments</span></div>
    <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', gap: 3, height: 14 }}>
        <span style={{ width: '62%', background: 'var(--green)', borderRadius: '7px 0 0 7px' }}></span>
        <span style={{ width: '22%', background: 'var(--yellow)' }}></span>
        <span style={{ width: '16%', background: RISK, borderRadius: '0 7px 7px 0' }}></span>
      </div>
      <div style={{ display: 'flex', gap: 16, fontSize: 12, color: 'var(--ink-3)' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: 'var(--green)' }}></span>Safe 154</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: 'var(--yellow)' }}></span>Review 56</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: RISK }}></span>Risk 38</span>
      </div>
      <div style={{ border: '1px solid var(--line)', borderLeft: `3px solid ${RISK}`, borderRadius: 10, padding: 13, background: 'var(--bg-soft)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: RISK }}>Segment 63</span>
          <span style={{ fontSize: 9.5, fontWeight: 700, color: '#b53e3e', background: '#fbe1e1', padding: '2px 8px', borderRadius: 999 }}>Terminology</span>
          <span style={{ fontSize: 9.5, fontWeight: 700, color: '#a87a14', background: 'var(--yellow-soft)', padding: '2px 8px', borderRadius: 999 }}>Fluency</span>
          <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 700, color: RISK }}>QE 41</span>
        </div>
        <div style={{ fontSize: 13, color: 'var(--ink)', lineHeight: 1.5 }}>"sperren" should be the locked term <b>"sichern"</b> per the Acme glossary.</div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ background: 'var(--green-soft)', color: 'var(--green)', fontWeight: 700, fontSize: 12.5, padding: '7px 13px', borderRadius: 9, display: 'inline-flex', alignItems: 'center', gap: 7 }}>{FI.check} Auto-approve 154 safe</span>
        <span style={{ fontSize: 11.5, color: 'var(--ink-4)' }}>94 left for review</span>
      </div>
    </div>
  </div>
);

const AQReasonVisual = () => (
  <div className="mock-frame">
    <div className="mock-chrome"><span className="mc-dot"></span><span className="mc-dot"></span><span className="mc-dot"></span><span className="mc-title">Segment 112 · EN → DE</span></div>
    <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.05em', color: 'var(--ink-4)', textTransform: 'uppercase' }}>Source</div>
        <div style={{ fontSize: 13.5, color: 'var(--ink-2)', marginTop: 3 }}>Save your changes before you leave.</div>
      </div>
      <div style={{ border: '1px solid color-mix(in oklab, var(--green) 30%, var(--line))', borderRadius: 10, padding: 12, background: 'color-mix(in oklab, var(--green) 5%, var(--bg-card))' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.05em', color: 'var(--green)', textTransform: 'uppercase' }}>Target · Rush AI</span>
          <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 700, color: 'var(--green)', background: 'var(--green-soft)', padding: '2px 9px', borderRadius: 999 }}>QE 96 · Safe</span>
        </div>
        <div style={{ fontSize: 14, color: 'var(--ink)', fontWeight: 500, marginTop: 6 }}>Speichern Sie Ihre Änderungen, bevor Sie gehen.</div>
      </div>
      <div style={{ borderTop: '1px solid var(--line)', paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.04em', color: 'var(--ink-4)', textTransform: 'uppercase' }}>Why this score</div>
        {[['Terminology', 'Matches glossary: "Änderungen", "speichern".'], ['Tags & length', 'All variables intact · within 8% of source length.'], ['Fluency', 'Native-grammatical, formal register honoured.']].map(([k, v]) => (
          <div key={k} style={{ display: 'flex', gap: 9, alignItems: 'flex-start', fontSize: 12.5 }}>
            <span style={{ color: 'var(--green)', marginTop: 1, flexShrink: 0 }}>{FI.check}</span>
            <span><b>{k}</b> <span style={{ color: 'var(--ink-3)' }}>· {v}</span></span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const RushFeatureAiQuality = () => {
  useRushReveal();
  return (
    <div className="rs">
      <RushNav active="features" />
      <FeatHero
        tint="green"
        kicker="AI quality"
        kickerIcon={FI.gauge}
        title="Trust the AI — verifiably."
        lead="Every draft is scored for adequacy, fluency, and risk before a human ever looks. Auto-approve what's provably safe, and send only the segments that genuinely need a person to your team — with the reasoning attached."
        primary={{ label: 'Get early access', to: '/register' }}
        secondary={{ label: 'See all features', to: '/features' }}
        meta={['Per-segment scoring', 'Color-coded risk', 'No black box']}
        visual={<AQHeroVisual />}
      />

      <FeatCapabilities
        tint="green"
        eyebrow="What it does"
        title="Know what's safe before you read it."
        lead="A quality estimate and a set of risk flags on every single segment — so review time goes where it actually matters."
        caps={[
          { icon: FI.gauge, title: 'Quality estimate', desc: "A per-segment confidence score (MTQE) so you know what's solid before review." },
          { icon: FI.alert, title: 'Risk flags', desc: 'Terminology, tag, length, and fluency issues surfaced and color-coded by severity.' },
          { icon: FI.bolt, title: 'Auto-approve threshold', desc: 'Set the confidence bar; clear the safe segments in one click.' },
          { icon: FI.search, title: 'Why this score', desc: 'Plain-language reasoning on every flag — never a number with no explanation.' },
        ]}
      />

      <FeatDetailRow
        reverse
        eyebrow="Explainable by default"
        title="Every score comes with its reasons."
        intro="A quality number on its own is just another thing to distrust. Rush Studio shows the terminology, tag, length, and fluency checks behind each score — so when it says a segment is safe, you can see exactly why, and when it flags one, you know what to fix."
        points={[
          { icon: FI.check, title: 'Terminology checks', desc: 'Glossary and do-not-translate matches verified against the segment.' },
          { icon: FI.layers, title: 'Tags & length', desc: 'Variables and markup intact; target length kept within a safe band of the source.' },
          { icon: FI.ai, title: 'Fluency assessment', desc: 'Grammar and register evaluated natively, not just word-for-word.' },
        ]}
        replaces={['Reviewing every single segment', 'Guessing which MT to trust']}
        visual={<AQReasonVisual />}
      />

      <FeatSteps
        eyebrow="How it works"
        title="Less reviewing. More shipping."
        steps={[
          { h: 'Score every draft', p: 'The moment the AI drafts a segment, it gets a quality estimate and a set of risk flags.' },
          { h: 'Set your threshold', p: 'Decide how confident is confident enough — then auto-approve everything above the bar.' },
          { h: 'Review only what matters', p: 'Your team sees the flagged segments, with the reasoning attached. Nothing safe wastes their time.' },
        ]}
      />

      <FeatQuote quote="We auto-approve about 60% of segments now. Reviewers finally spend their time on the calls that actually need judgment." initials="AK" name="Anja Klein" role="Head of localization, Klein/Co" />

      <FeatCrossLinks current="ai-quality" />

      <FeatFinalCTA title="Review only what needs a human." sub="Join the waitlist for early-access pricing and a 1:1 onboarding call with our team." />

      <RushFooter />
    </div>
  );
};

export default RushFeatureAiQuality;
